import { getChatApiBase } from "@/apiBase";

export type ChatGuestState = "too_early" | "waiting_host" | "joinable" | "ended" | "revoked" | string;

export type ChatGuestPreview = {
  state: ChatGuestState;
  host_name: string;
  title: string;
  when_label?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  opens_at?: string | null;
  session_at?: number | null;
  server_now?: string | null;
  early_minutes?: number;
  suggested_name?: string;
};

export type ChatGuestJoinResult = {
  livekit_url: string;
  token: string;
  room: string;
  call_id: string;
  identity: string;
  display_name: string;
  host_name: string;
  title: string;
};

export type ChatGuestInvite = {
  id: number;
  email: string;
  display_name: string;
  status: string;
  meeting_id: number | null;
  call_id: string | null;
  lobby_at?: string | null;
  session_starts_at?: string | null;
};

/** Destinataire de l’invitation (e-mail envoyé), pas « qui a ouvert ». */
export function guestInviteListTitle(g: ChatGuestInvite): string {
  const email = (g.email || "").trim();
  const name = (g.display_name || "").trim();
  if (email && name && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} · ${email}`;
  }
  if (email) return email;
  if (name) return name;
  return `Lien #${g.id}`;
}

export function guestInvitePresenceLabel(g: ChatGuestInvite): string {
  if (g.status === "joined") return "en visio";
  if (g.status === "left") return "hors visio";
  if (g.lobby_at) return "a ouvert le lien";
  return emailOrLinkPendingLabel(g);
}

function emailOrLinkPendingLabel(g: ChatGuestInvite): string {
  return (g.email || "").trim()
    ? "invitation envoyée — pas encore ouvert"
    : "lien créé — pas encore ouvert";
}

function guestRank(g: ChatGuestInvite): number {
  if (g.status === "joined") return 4;
  if (g.lobby_at) return 3;
  if (g.status === "pending") return 2;
  if (g.status === "left") return 1;
  return 0;
}

/** Une ligne par e-mail (la plus récente / la plus « active »). Ignore les vieux liens sans e-mail déjà partis. */
export function dedupeChatGuests(guests: ChatGuestInvite[]): ChatGuestInvite[] {
  const byKey = new Map<string, ChatGuestInvite>();
  for (const g of guests) {
    if (g.status !== "pending" && g.status !== "joined" && g.status !== "left") continue;
    const email = (g.email || "").trim().toLowerCase();
    if (!email && g.status === "left") continue;
    const key = email || `id:${g.id}`;
    const prev = byKey.get(key);
    if (
      !prev ||
      guestRank(g) > guestRank(prev) ||
      (guestRank(g) === guestRank(prev) && g.id > prev.id)
    ) {
      byKey.set(key, g);
    }
  }
  return [...byKey.values()];
}

type ApiResult<T> = {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
};

function chatUrl(path: string): string | null {
  const base = getChatApiBase();
  if (!base) return null;
  const absoluteBase = /^https?:\/\//i.test(base)
    ? base.replace(/\/?$/, "/")
    : `${typeof window !== "undefined" ? window.location.origin : "http://localhost"}${base.startsWith("/") ? base : `/${base}`}/`;
  return new URL(path.replace(/^\//, ""), absoluteBase).href;
}

async function postJson<T>(
  suiteUserId: string | null,
  body: Record<string, unknown>
): Promise<ApiResult<T>> {
  const url = chatUrl("guests.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (suiteUserId && /^\d+$/.test(suiteUserId)) {
    headers["X-User-Id"] = suiteUserId;
  }
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { success: true, data: data as T };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

export { isChatGuestJoin, readGuestToken } from "@/lib/guestToken";

export async function previewChatGuest(token: string): Promise<ApiResult<ChatGuestPreview>> {
  return postJson<ChatGuestPreview>(null, { action: "preview", token });
}

export async function joinChatGuest(
  token: string,
  displayName: string
): Promise<ApiResult<ChatGuestJoinResult>> {
  return postJson<ChatGuestJoinResult>(null, {
    action: "join",
    token,
    display_name: displayName,
  });
}

export async function lobbyChatGuest(
  token: string,
  displayName: string
): Promise<ApiResult<{ state?: string; notified?: boolean }>> {
  return postJson(null, {
    action: "lobby",
    token,
    display_name: displayName,
  });
}

export async function touchChatGuest(token: string): Promise<ApiResult<{ state?: string }>> {
  return postJson(null, { action: "touch", token });
}

export async function leaveChatGuest(token: string): Promise<ApiResult<unknown>> {
  return postJson(null, { action: "leave", token });
}

export async function createChatGuestInvite(
  suiteUserId: string | null,
  payload: {
    conversationId: number;
    email?: string;
    meetingId?: number;
    callId?: string;
    startsAt?: string | null;
  }
): Promise<ApiResult<{ invite_id: number; join_url: string; mailed: boolean }>> {
  const r = await postJson<{ invite_id?: number; join_url?: string; mailed?: boolean }>(
    suiteUserId,
    {
      action: "create",
      conversation_id: payload.conversationId,
      ...(payload.email ? { email: payload.email } : {}),
      ...(payload.meetingId ? { meeting_id: payload.meetingId } : {}),
      ...(payload.callId ? { call_id: payload.callId } : {}),
      ...(payload.startsAt ? { starts_at: payload.startsAt } : {}),
    }
  );
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: {
      invite_id: Number(r.data.invite_id) || 0,
      join_url: String(r.data.join_url || ""),
      mailed: Boolean(r.data.mailed),
    },
  };
}

export async function listChatGuestInvites(
  suiteUserId: string | null,
  conversationId: number,
  meetingId?: number
): Promise<ApiResult<ChatGuestInvite[]>> {
  const r = await postJson<{ guests?: ChatGuestInvite[] }>(suiteUserId, {
    action: "list",
    conversation_id: conversationId,
    ...(meetingId ? { meeting_id: meetingId } : {}),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: Array.isArray(r.data.guests) ? r.data.guests : [] };
}

export async function reinviteChatGuest(
  suiteUserId: string | null,
  conversationId: number,
  inviteId: number
): Promise<ApiResult<{ invite_id: number; join_url: string; mailed: boolean }>> {
  const r = await postJson<{ invite_id?: number; join_url?: string; mailed?: boolean }>(
    suiteUserId,
    {
      action: "reinvite",
      conversation_id: conversationId,
      invite_id: inviteId,
    }
  );
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: {
      invite_id: Number(r.data.invite_id) || inviteId,
      join_url: String(r.data.join_url || ""),
      mailed: Boolean(r.data.mailed),
    },
  };
}

export async function revokeChatGuestInvite(
  suiteUserId: string | null,
  conversationId: number,
  inviteId: number
): Promise<ApiResult<unknown>> {
  return postJson(suiteUserId, {
    action: "revoke",
    conversation_id: conversationId,
    invite_id: inviteId,
  });
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
